import { action, autorun, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { createContext, useContext } from 'react';
import { getGroupsPage } from 'api/operations/groupService';
import { GroupFilter, GroupPageParams, GroupSortBy, SortOrder } from 'types/groupService';
import { getEnrollmentPeriodsByGroupIds } from 'api/operations/enrollmentService';
import { InboundLeadModalStore } from 'store/ui-stores/dashboard/inboundLeadModalStore';
import { getRootStoreInstance } from 'store/rootStore';
import { adminRights, celticsRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { GroupInteractionDelegate } from 'store/pages/groups-dashboard/groupInteractionDelegate';
import { mapEnrollmentsToGroups } from 'global/services-helpers/enrollmentService';
import { DashboardGroupDelegate } from './dashboardGroupDelegate';
import { ConfirmNewGroupModalDelegate } from './confirmNewGroupModalDelegate';

export class GroupsDashboardPageStore {
  private _rootStore = getRootStoreInstance();
  private _isLoading = false;
  private _groups: DashboardGroupDelegate[] = [];

  private _groupRequestParams: GroupPageParams = {
    filter: GroupFilter.ALL,
    sortOrder: SortOrder.DESC,
    sortBy: GroupSortBy.UPDATE_TIME,
    fullView: false,
    page: 1,
  };

  private _totalPages: number = 1;

  // UI stores
  public confirmNewGroupModalDelegate = new ConfirmNewGroupModalDelegate();
  public inboundLeadModalStore = new InboundLeadModalStore();
  public groupInteractionDelegate = new GroupInteractionDelegate({
    setPageLoading: this.setLoading.bind(this),
    updatePageData: this.getInitialData.bind(this),
  });

  get isLoading(): boolean {
    return this._isLoading;
  }

  get groups(): DashboardGroupDelegate[] {
    return this._groups;
  }

  get groupRequestParams(): GroupPageParams {
    return this._groupRequestParams;
  }

  get totalPages(): number {
    return this._totalPages;
  }

  public async getInitialData(): Promise<void> {
    this.setLoading(true);
    const groups = await getGroupsPage(this._groupRequestParams);
    this.setTotalPages(groups.totalPages);
    await runInAction(async () => {
      const enrollments = await getEnrollmentPeriodsByGroupIds(
        groups.result.map((group) => group.id),
        this._groupRequestParams.filter === GroupFilter.SOLD,
      );

      runInAction(() => {
        this._groups = groups.result.map(
          (group) =>
            new DashboardGroupDelegate({
              group,
              inboundLeadModalStore: this.inboundLeadModalStore,
              groupInteractionDelegate: this.groupInteractionDelegate,
              enrollments: mapEnrollmentsToGroups(enrollments)[group.id],
            }),
        );
      });
      this.setLoading(false);
    });
  }

  public setLoading(status: boolean): void {
    this._isLoading = status;
  }

  setFilter(filterValue: GroupFilter): void {
    this.setLoading(true);
    this._groupRequestParams.filter = filterValue;
  }

  setSortOrder(sortOrder: SortOrder): void {
    this.setLoading(true);
    this._groupRequestParams.sortOrder = sortOrder;
  }

  setSortBy(sortBy: GroupSortBy): void {
    this.setLoading(true);
    this._groupRequestParams.sortBy = sortBy;
  }

  flipFullView(): void {
    this.setLoading(true);
    this._groupRequestParams.fullView = !this._groupRequestParams.fullView;
  }

  setPage(page: number): void {
    this.setLoading(true);
    this._groupRequestParams.page = page;
  }

  setTotalPages(totalPages: number): void {
    this._totalPages = totalPages;
  }

  constructor() {
    makeObservable<
      GroupsDashboardPageStore,
      '_rootStore' | '_groups' | '_isLoading' | '_groupRequestParams' | '_totalPages'
    >(this, {
      _rootStore: observable,
      _isLoading: observable,
      _groups: observable,
      _groupRequestParams: observable.shallow,
      _totalPages: observable,
      totalPages: computed,
      groups: computed,
      isLoading: computed,
      groupRequestParams: computed,
      getInitialData: action.bound,
      setLoading: action.bound,
      setFilter: action.bound,
      setSortOrder: action.bound,
      setSortBy: action.bound,
      flipFullView: action.bound,
      setTotalPages: action.bound,
      setPage: action.bound,
    });

    autorun(() => {
      const roles = toJS(this._rootStore.userStore.userRoles);
      const highUser = celticsRights(toJS(roles)) || adminRights(toJS(roles));

      if (highUser) {
        this._groupRequestParams.fullView = true;
      }
    });
  }
}

const GroupsDashboardPageStoreContext = createContext(new GroupsDashboardPageStore());

const useGroupDashboardPageStore = (): GroupsDashboardPageStore => {
  return useContext(GroupsDashboardPageStoreContext);
};

export { GroupsDashboardPageStoreContext, useGroupDashboardPageStore };
